<script setup lang="ts">
import { computed, ref, useTemplateRef, watchEffect } from "vue";

import ReportMetric from "@/components/datascience/ReportMetric.vue";
import CopyableImageWidget from "@/components/ui/CopyableImageWidget.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";
import {
  isCrossValidationReport,
  type CrossValidationReportDto,
  type EstimatorReportDto,
  type MetricDto,
} from "@/dto";
import type { MetricDtoViewModel } from "@/models";
import { urlToBase64 } from "@/services/utils";
import { useDatasourceStore } from "@/stores/datasource";

const COMPUTE_METRICS = ["predict_time", "fit_time"];

const { report, parentReport } = defineProps<{
  report: EstimatorReportDto | CrossValidationReportDto;
  parentReport?: CrossValidationReportDto;
}>();

const datasource = useDatasourceStore();
const expandableCard = useTemplateRef<InstanceType<typeof ExpandableCard>>("expandableCard");

const plots = ref<{ datasource: string | null; base64Src: string; name: string }[]>([]);

const metricsByName = computed(() => {
  const currentMetrics = report.metrics.filter(
    (m) => m.data_source === datasource.sourceName || m.data_source === null
  );

  const parentMetrics = parentReport?.metrics ?? [];
  const versusMeanByName = new Map(
    [...parentMetrics, ...currentMetrics]
      .filter((m) => m.name.includes("_mean"))
      .map((m) => [m.name.replace("_mean", ""), m.value])
  );

  const stdByName = new Map(
    currentMetrics
      .filter((m) => m.name.includes("_std"))
      .map((m) => [m.name.replace("_std", ""), m.value])
  );

  const predicate = isCrossValidationReport(report)
    ? (m: MetricDto) => !m.name.includes("_std") && m.name.includes("_mean")
    : (m: MetricDto) => !m.name.includes("_std") && !m.name.includes("_mean");

  const r = new Map<string, MetricDtoViewModel>();
  for (const metric of currentMetrics.filter(predicate)) {
    const name = metric.name.replace("_mean", "");
    const vm: MetricDtoViewModel = {
      ...metric,
      std: stdByName.get(name) ?? null,
      versusMean: versusMeanByName.get(name) ?? null,
    };
    r.set(name, vm);
  }

  return r;
});

const computationalMetricsByName = computed(() => {
  return new Map(
    [...metricsByName.value.entries()]
      .filter(([key]) => COMPUTE_METRICS.includes(key))
      .sort(([, a], [, b]) => (a.position ?? Number.MAX_VALUE) - (b.position ?? Number.MAX_VALUE))
  );
});

const statisticalMetricsByName = computed(() => {
  return new Map(
    [...metricsByName.value.entries()]
      .filter(([key]) => !COMPUTE_METRICS.includes(key))
      .sort(([, a], [, b]) => (a.position ?? Number.MAX_VALUE) - (b.position ?? Number.MAX_VALUE))
  );
});

const visiblePlots = computed(() => {
  return plots.value.filter((p) => p.datasource === datasource.sourceName);
});

function expand(): Promise<void> {
  return expandableCard.value?.expand() ?? Promise.resolve();
}

watchEffect(async () => {
  const medias = report.medias.filter(
    (m) => m.category === "performance" && m.content_type === "image/svg+xml"
  );
  const promises = medias.map(async (m) => ({
    datasource: m.data_source,
    base64Src: await urlToBase64(m.presigned_url),
    name: m.name,
  }));
  plots.value = await Promise.all(promises);
});

defineExpose({ expand });
</script>

<template>
  <div class="report-metrics">
    <ExpandableCard
      ref="expandableCard"
      title="General metrics"
      icon="icon-presentation"
      :is-expandable="visiblePlots.length > 0"
    >
      <template #actions>
        <slot name="actions" />
      </template>

      <div class="metrics-container">
        <section v-if="computationalMetricsByName.size > 0">
          <div class="name text-base-regular">Compute</div>
          <ReportMetric
            v-for="[key, metric] of computationalMetricsByName.entries()"
            :key="key"
            :metric="metric"
          />
        </section>

        <section v-if="statisticalMetricsByName.size > 0">
          <div class="name text-base-regular">Statistical</div>
          <ReportMetric
            v-for="[key, metric] of statisticalMetricsByName.entries()"
            :key="key"
            :metric="metric"
          />
        </section>
      </div>

      <template #expandableContent>
        <div class="card-content plots-container">
          <div
            class="plot"
            v-for="(plot, index) in visiblePlots"
            :key="index"
            :id="plot.name.replace(/_/g, '-')"
          >
            <CopyableImageWidget
              mediaType="image/svg+xml"
              :base64-src="plot.base64Src"
              :scale="2"
            />
          </div>
        </div>
      </template>
    </ExpandableCard>
  </div>
</template>

<style scoped>
.report-metrics {
  --nb-columns: 2;

  container-name: container;
  container-type: inline-size;

  .metrics-container {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-32);

    section {
      display: grid;
      gap: var(--numbers-8) var(--numbers-16);
      grid-template-columns: repeat(var(--nb-columns), 1fr);

      .name {
        color: var(--content-secondary);
        grid-column: 1 / -1;
      }
    }
  }

  .plots-container {
    display: grid;
    gap: var(--numbers-8) var(--numbers-16);
    grid-template-columns: repeat(var(--nb-columns), 1fr);

    .plot {
      overflow: hidden;
      border-radius: var(--numbers-16);

      .copyable-image-widget {
        display: flex;
        height: 100%;
        align-items: center;
        justify-content: flex-start;
      }
    }

    &:has(.plot:only-child) {
      grid-template-columns: 1fr;
    }
  }

  @container container (width <= 500px) {
    section {
      --nb-columns: 1;
    }
  }
}
</style>
